import { useEffect, useState, type ReactElement } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  getSsoConfig,
  getSsoSession,
  redirectToSsoLogin,
  storeSsoSession,
  validateSsoToken,
} from 'auth/ssoClient'
import { PageLoader } from 'components/common/Loader'
import { decryptQueryParams } from 'utils/crypto'

const SSO_LINK_PARAMS = ['token', 'ssotoken', 'sso_token', 'kit_token', 'deviceid', 'returnurl']

/** The query string without the SSO hand-off params; entity params (entityType, q, …) stay. */
function withoutSsoParams(search: string): string {
  const params = new URLSearchParams(search)
  for (const key of [...params.keys()]) {
    if (SSO_LINK_PARAMS.includes(key.toLowerCase())) params.delete(key)
  }
  const rest = params.toString()
  return rest ? `?${rest}` : ''
}

/**
 * Route guard: the entity screens need an identity to call the API as.
 * Accepted, in order:
 *   1. an SSO token in the link (`?token=…`, the way the main Kit19 app opens
 *      View / Edit entity) -> validated through Authorize/ValidateToken and the
 *      returned user becomes the session. This happens in place, behind a loader:
 *      the user never sees a sign-in page;
 *   2. the (encrypted) link carries UserId/ParentId itself;
 *   3. an SSO session already stored in localStorage;
 *   4. SSO disabled -> development identity.
 * Only a visit with none of these goes to /login, which forwards to the Kit19 SSO page.
 */
export function RequireSession({ children }: { children: ReactElement }) {
  const location = useLocation()
  const navigate = useNavigate()
  const sso = getSsoConfig()

  const params = decryptQueryParams(location.search)
  const lowered = new Map(Object.entries(params).map(([k, v]) => [k.toLowerCase(), v]))
  const linkToken =
    lowered.get('token') || lowered.get('ssotoken') || lowered.get('sso_token') || lowered.get('kit_token')
  const deviceId = lowered.get('deviceid') || null

  const session = getSsoSession()
  const tokenIsCurrentSession = !!linkToken && linkToken === session.token && session.isAuthenticated

  // The first paint for a link that carries a token must be the loader. Starting this
  // as false let the /login redirect below win the race on a first visit, so the
  // token in the link was never used.
  const [validating, setValidating] = useState<boolean>(() => sso.enabled && !!linkToken && !tokenIsCurrentSession)
  const [error, setError] = useState<string | null>(null)

  const cleanPath = location.pathname + withoutSsoParams(location.search)

  useEffect(() => {
    if (!sso.enabled || !linkToken) return
    if (tokenIsCurrentSession) {
      // already signed in as this user: just keep the JWT out of the address bar
      navigate(cleanPath, { replace: true })
      return
    }

    let cancelled = false
    setValidating(true)
    setError(null)

    void (async () => {
      const result = await validateSsoToken(linkToken)
      if (cancelled) return

      if (result.valid && result.userData) {
        storeSsoSession(linkToken, result.userData, deviceId)
        setValidating(false)
        navigate(cleanPath, { replace: true })
      } else {
        setError(result.message || 'The sign-in token in this link could not be validated.')
        setValidating(false)
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkToken, deviceId, tokenIsCurrentSession, sso.enabled])

  if (!sso.enabled) return children

  if (validating) return <PageLoader />

  if (error) {
    return (
      <div className="app-shell">
        <div className="auth-error card">
          <h2>Sign-in failed</h2>
          <p>{error}</p>
          <p className="text-muted font12">
            Open the record again from Kit19, or sign in to continue to this page.
          </p>
          <button type="button" className="btn pri-btn" onClick={() => redirectToSsoLogin(cleanPath)}>
            Sign in with Kit19
          </button>
        </div>
      </div>
    )
  }

  const linkHasIdentity = !!(lowered.get('userid') || lowered.get('uid'))

  if (linkHasIdentity || getSsoSession().isAuthenticated) {
    return children
  }

  return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
}
